import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { disposeApi, getApiErrorMessage } from "@/api";
import { Subtitle } from "@/components/dialog/DialogLayout";
import { CreateInviteLinkButton } from "@/components/dialog/team/CreateInviteLinkButton";
import { FormFieldError } from "@/components/form/FormField";
import { Icon } from "@/components/icons/Icon";
import { ListItem } from "@/components/list/ListItem";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { UserListItem } from "@/components/user/UserListItem";
import { Email, UserSearchInput } from "@/components/user/UserSearchInput";
import { twMerge } from "@/utils/twMerge";

import type { InviteModel } from "@/models/InviteModel";
import type { TeamModel } from "@/models/TeamModel";
import type React from "react";

type InvitesContext = "team-dialog" | "create-team-dialog";

const LinkText = ({ value }: { value: string }) => {
  const t = useTranslations();
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (linkCopied) {
      const timeout = setTimeout(() => setLinkCopied(false), 1000);
      return () => clearTimeout(timeout);
    }
  }, [linkCopied]);

  const handleClick = async () => {
    await navigator.clipboard.writeText(value);
    setLinkCopied(true);
  };

  return (
    <button
      aria-label={t("teamDialog.copyLink")}
      type="button"
      className={twMerge(
        "flex flex-row items-center min-w-0",
        "hover:opacity-60",
        "active:opacity-40",
      )}
      onClick={handleClick}
    >
      <div className={twMerge("flex w-8 h-8 mb-auto", "sm:w-10 sm:h-10")}>
        <Icon name="Link" className="m-auto" />
      </div>
      <div
        className={twMerge(
          "relative flex flex-1 flex-col min-w-0 ml-1.5 my-auto py-1.5",
          "leading-[1.2] tracking-tight",
        )}
      >
        <div className={twMerge("flex-1 truncate -mt-0.5 text-[16px] text-left font-normal")}>
          {value}
        </div>
      </div>
      <div className={twMerge("flex w-8 h-8 mb-auto", "sm:w-10 sm:h-10")}>
        {linkCopied ? (
          <Icon name="Check" className="m-auto" />
        ) : (
          <Icon name="Copy" className="m-auto" />
        )}
      </div>
    </button>
  );
};

const InviteWrapper = ({
  children,
  errorMessage,
}: {
  children: React.ReactNode;
  errorMessage: string;
}) => {
  return (
    <>
      <div className="flex flex-row items-center min-w-0 min-h-12">{children}</div>
      <FormFieldError message={errorMessage} className="mt-0" />
    </>
  );
};

const InviteDropdown = ({
  invite,
  onError,
  context,
}: {
  invite: InviteModel;
  onError: (error: string) => void;
  context: InvitesContext;
}) => {
  const t = useTranslations();
  const [showSpinner, setShowSpinner] = useState(false);

  const handleResendClick = async () => {
    setShowSpinner(true);
    try {
      await invite.resend();
    } catch (err) {
      onError(getApiErrorMessage(err));
    }
    setShowSpinner(false);
  };

  const handleRevokeClick = async () => {
    setShowSpinner(true);
    try {
      await invite.revoke();
    } catch (err) {
      onError(getApiErrorMessage(err));
    }
    setShowSpinner(false);
  };

  const handleCopyLinkClick = async () => {
    if (invite.link) {
      await navigator.clipboard.writeText(invite.link);
    }
  };

  const items = [
    ...(invite.user || invite.email
      ? [
          {
            key: "resend",
            title: t("teamDialog.resend"),
            onClick: () => handleResendClick(),
          },
        ]
      : []),
    ...(invite.link
      ? [
          {
            key: "copy-link",
            title: t("teamDialog.copyLink"),
            onClick: () => handleCopyLinkClick(),
          },
        ]
      : []),
    {
      key: "revoke",
      title: t("teamDialog.revoke"),
      onClick: () => handleRevokeClick(),
    },
  ];

  if (showSpinner) {
    return (
      <div className="flex h-9.5">
        <Icon name="Spinner" className="m-auto animate-spin" />
      </div>
    );
  }

  if (context === "create-team-dialog") {
    return (
      <Sidebar.Button
        inverted
        aria-label={t("teamDialog.revoke")}
        className="w-auto ml-auto text-gray-500"
        onClick={() => handleRevokeClick()}
      >
        <Icon name="Cross" className="w-[24px] h-[24px] [&>path]:stroke-gray-500" />
      </Sidebar.Button>
    );
  }

  const toggleText = invite.user || invite.email ? t("teamDialog.invited") : "";

  if (toggleText) {
    return (
      <Dropdown
        anchor="bottom end"
        toggleButtonLabel={t("teamDialog.inviteDropdown")}
        toggleText={toggleText}
        toggleTextInvertedBg
        items={items}
        itemsClassName="min-w-[200px]"
      />
    );
  }

  return (
    <Dropdown
      anchor="bottom end"
      toggleButtonComponent={
        toggleText ? undefined : (
          <Sidebar.Button
            as="div"
            inverted
            aria-label={t("teamDialog.inviteDropdown")}
            className={twMerge("text-gray-500 text-[15px] leading-[1.2]", "sm:text-[17px]")}
          >
            <Icon
              name="ArrowDownLarge"
              width={16}
              height={16}
              className="ml-auto [&>path]:stroke-document-text"
            />
          </Sidebar.Button>
        )
      }
      items={items}
      itemsClassName="min-w-[200px]"
    />
  );
};

const Invite = ({ invite, context }: { invite: InviteModel; context: InvitesContext }) => {
  const { user, email } = invite;
  const [serverError, setServerError] = useState("");

  const handleError = (error: string) => {
    setServerError(error);
  };

  if (user) {
    return (
      <InviteWrapper errorMessage={serverError}>
        <UserListItem
          isLink
          user={user}
          rightController={
            <InviteDropdown invite={invite} onError={handleError} context={context} />
          }
        />
      </InviteWrapper>
    );
  }

  if (email) {
    return (
      <InviteWrapper errorMessage={serverError}>
        <ListItem
          content={<Email email={email} />}
          rightController={
            <InviteDropdown invite={invite} onError={handleError} context={context} />
          }
        />
      </InviteWrapper>
    );
  }

  if (invite.link) {
    return (
      <InviteWrapper errorMessage={serverError}>
        <ListItem
          content={<LinkText value={invite.link} />}
          rightController={
            <InviteDropdown invite={invite} onError={handleError} context={context} />
          }
        />
      </InviteWrapper>
    );
  }

  return null;
};

const InvitesList = observer(function InvitesList({
  team,
  context,
}: {
  team: TeamModel;
  context: InvitesContext;
}) {
  const { invitesUX } = team;
  const t = useTranslations();

  const renderInvite = (invite: InviteModel) => {
    return <Invite key={invite.token} invite={invite} context={context} />;
  };

  if (invitesUX.invitesStatus === "loading") {
    return (
      <div className="flex items-center justify-center p-10">
        <Icon name="Spinner" className="animate-spin" />
      </div>
    );
  }

  if (invitesUX.visibleInvites.length === 0 && invitesUX.linkInvites.length === 0) {
    if (context === "create-team-dialog") {
      return (
        <div data-testid="invites-list" className="w-full mb-2.5">
          <CreateInviteLinkButton team={team} className="w-full mb-2.5" />
          <div className="h-[1px] mx-2.5 mt-2.5 bg-block-border" />
        </div>
      );
    }
    return null;
  }

  return (
    <div data-testid="invites-list" className="w-full mb-5">
      {invitesUX.visibleInvites.length > 0 && (
        <>
          <Subtitle title={t("teamDialog.invitesTitle")} className="mb-2.5" />
          {invitesUX.visibleInvites.map(renderInvite)}
        </>
      )}
      {invitesUX.linkInvites.length > 0 && (
        <>
          <Subtitle title={t("teamDialog.inviteLinksTitle")} className="my-2.5" />
          {invitesUX.linkInvites.map(renderInvite)}
        </>
      )}
      {context === "create-team-dialog" && (
        <>
          <CreateInviteLinkButton team={team} className="w-full my-2.5" />
          <div className="h-[1px] mx-2.5 mt-2.5 bg-block-border" />
        </>
      )}
    </div>
  );
});

export const Invites = observer(function Invites({
  team,
  context = "team-dialog",
}: {
  team: TeamModel;
  context?: InvitesContext;
}) {
  const t = useTranslations();

  useEffect(() => {
    return disposeApi(async (signal) => {
      await team.invitesUX.loadInvites(signal);
    });
  }, [team]);

  return (
    <>
      <div className={twMerge("flex items-center gap-2.5 mb-5", "sm:flex-col sm:items-stretch")}>
        <UserSearchInput
          className="flex-1 min-w-0"
          onUserClick={(user) => team.invitesUX.createInvite({ username: user.username })}
          onEmailClick={(email) => team.invitesUX.createInvite({ email })}
          allowMultipleEmails
          submitAllLabel={(count) => t("teamDialog.inviteAll", { count })}
        />
        {context === "team-dialog" && <CreateInviteLinkButton team={team} className="shrink-0" />}
      </div>
      <InvitesList team={team} context={context} />
    </>
  );
});
